import type { JSX } from 'react';
import { type UseFormRegister, type FieldErrors } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CreateQuestionFormData } from '../schemas/referee-question.schemas';
import type {
  CreateRefereeQuestionRequest,
  RefereeQuestion,
} from '../types/referee-question.types';
import { useQuestionForm } from '../hooks/useQuestionForm';

interface CreateQuestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateRefereeQuestionRequest) => void;
  onUpdate?: (data: { questionId: string; text: string; isRequired: boolean }) => void;
  editingQuestion?: RefereeQuestion | null;
  isLoading?: boolean;
}

interface QuestionFormFieldsProps {
  register: UseFormRegister<CreateQuestionFormData>;
  errors: FieldErrors<CreateQuestionFormData>;
}

function QuestionFormFields({ register, errors }: QuestionFormFieldsProps): JSX.Element {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="text">Question Text</Label>
        <Input id="text" placeholder="Enter question text..." {...register('text')} />
        {errors.text && <p className="text-sm text-destructive">{errors.text.message}</p>}
      </div>
      <div className="flex items-center space-x-2">
        <input type="checkbox" id="isRequired" {...register('isRequired')} className="h-4 w-4" />
        <Label htmlFor="isRequired">Required</Label>
      </div>
    </div>
  );
}

export function CreateQuestionDialog({
  open,
  onOpenChange,
  onSubmit,
  onUpdate,
  editingQuestion,
  isLoading,
}: CreateQuestionDialogProps): JSX.Element {
  const { register, errors, isEditMode, submitHandler, reset } = useQuestionForm(
    editingQuestion,
    onSubmit,
    onUpdate
  );

  const handleClose = (isOpen: boolean): void => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Question' : 'Add Question'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submitHandler}>
          <QuestionFormFields register={register} errors={errors} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isEditMode ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
